import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  autoPickedByPage,
  useEventsLookup,
} from '../../hooks/useEventsLookup';
import {
  EventLookupItem,
  isSelectableEvent,
  pickDefaultEvent,
  readPersistedPageEventId,
} from '../../helpers/selectedEvent';
import { activatePageEvent, setPageEvent } from '../../store/ducks/pageEvents';

interface Props {
  /** Page slug: each page keeps its own selected event under this key. */
  pageKey: string;
  children: React.ReactNode;
}

/**
 * Gives the page under `pageKey` its OWN selected event.
 *
 * Before the page renders (layout effect, no paint in between) it makes the
 * page the active one and sets `generalFilters.event` to the page's event:
 *   1. the event carried by a drill-down link (`location.state.syncFilters`),
 *   2. else the page's selection from earlier in this session,
 *   3. else its explicit choice saved in localStorage
 *      (`admin.selectedEvent.<pageKey>`) if that event still exists and is
 *      published,
 *   4. else the default (latest published event, `pickDefaultEvent`).
 * So the page's first fetch already uses its own event (no fetch with another
 * page's event, no second fetch when a default is applied). The selection is
 * re-validated when the events list arrives / refreshes.
 */
const PageEventScope: React.FC<Props> = ({ pageKey, children }) => {
  const dispatch = useDispatch();
  const store = useStore<any>();
  const location = useLocation();
  const { events, loaded, fresh } = useEventsLookup();
  const activePage: string = useSelector(
    (state: any) => state.pageEvents?.activePage || '',
  );
  const eventId: string = useSelector(
    (state: any) => state.generalFilters.event || '',
  );
  const [active, setActive] = useState(
    () => store.getState().pageEvents?.activePage === pageKey,
  );
  // A choice restored from localStorage while only the saved (possibly stale)
  // events list was available: checked strictly once the fresh list arrives.
  const strictCheckRef = useRef(false);

  useLayoutEffect(() => {
    const state = store.getState();
    if (state.pageEvents?.activePage !== pageKey) {
      const lookup: EventLookupItem[] = state.events.lookup || [];
      const lookupFresh = state.events.lookupLoadedAt > 0;
      const lookupKnown = lookupFresh || lookup.length > 0;
      const inMemory: string | undefined = state.pageEvents?.byPage?.[pageKey];
      const carried = (location.state as any)?.syncFilters
        ? state.generalFilters.event || ''
        : '';

      let initial = '';
      let fromStorage = false;
      if (carried) {
        initial = carried;
        autoPickedByPage.delete(pageKey);
      } else if (inMemory !== undefined) {
        initial = inMemory;
      } else {
        initial = readPersistedPageEventId(pageKey) ?? '';
        fromStorage = !!initial;
        if (
          fromStorage &&
          lookupFresh &&
          !isSelectableEvent(lookup.find((e) => e.id === initial))
        ) {
          // Saved event was deleted / unpublished: fall back to the default.
          initial = '';
          fromStorage = false;
        }
      }
      if (!initial && lookupKnown) {
        initial = pickDefaultEvent(lookup)?.id ?? '';
        if (initial) autoPickedByPage.set(pageKey, initial);
      }
      strictCheckRef.current = fromStorage && !lookupFresh;
      dispatch(activatePageEvent(pageKey, initial));
    }
    setActive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve / re-validate when the events list becomes known or changes.
  useEffect(() => {
    if (!active || activePage !== pageKey || !loaded) return;
    const strict = strictCheckRef.current && fresh;
    if (strict) strictCheckRef.current = false;

    const current = eventId ? events.find((e) => e.id === eventId) : undefined;
    const isAuto = !!eventId && autoPickedByPage.get(pageKey) === eventId;
    let keep: boolean;
    if (!eventId || isAuto) {
      // Nothing selected yet, or an automatic pick: follow the default rule.
      keep = false;
    } else if (strict) {
      keep = isSelectableEvent(current);
    } else {
      // Explicit choice: kept while the event exists (the list may be the
      // stale saved copy, or empty after a failed load).
      keep = !!current || !fresh || events.length === 0;
    }
    if (keep) return;

    const next = pickDefaultEvent(events)?.id ?? '';
    if (next) autoPickedByPage.set(pageKey, next);
    else autoPickedByPage.delete(pageKey);
    if (next !== eventId) dispatch(setPageEvent(pageKey, next, true));
  }, [active, activePage, pageKey, loaded, fresh, events, eventId, dispatch]);

  if (!active) return null;
  return <>{children}</>;
};

export default PageEventScope;
